// components/VideoPlayer/SaveCollectionModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import {
  uploadNonOnlineSubtitle,
  createCollection,
  searchCover,
  uploadCover,
} from "../../services/subtitleApi";
import "./SaveCollectionModal.css";
import {
  getLanguageName,
  normalizeLanguage,
  getMainLanguageOptions,
  getLanguageColor,
} from "../../utils/languages";

// کدهای زبان معتبر برای ارسال به بک‌اند
const VALID_LANGUAGE_CODES = ["fa", "en", "de", "fr", "it", "tr"];

// نگاشت معکوس: نام زبان به کد (برای تبدیل نام‌های فارسی به کد)
const LANGUAGE_NAME_TO_CODE = {
  فارسی: "fa",
  انگلیسی: "en",
  آلمانی: "de",
  فرانسوی: "fr",
  ایتالیایی: "it",
  ترکی: "tr",
  fa: "fa",
  en: "en",
  de: "de",
  fr: "fr",
  it: "it",
  tr: "tr",
  Farsi: "fa",
  English: "en",
  German: "de",
  French: "fr",
  Italian: "it",
  Turkish: "tr",
  persian: "fa",
  english: "en",
  german: "de",
  french: "fr",
  italian: "it",
  turkish: "tr",
};

const normalizeLanguageCode = (langString) => {
  if (!langString) return "";
  const lowerLang = langString.toLowerCase().trim();
  if (VALID_LANGUAGE_CODES.includes(lowerLang)) {
    return lowerLang;
  }
  const normalized = normalizeLanguage(lowerLang);
  if (VALID_LANGUAGE_CODES.includes(normalized)) {
    return normalized;
  }
  if (LANGUAGE_NAME_TO_CODE[lowerLang]) {
    return LANGUAGE_NAME_TO_CODE[lowerLang];
  }
  if (LANGUAGE_NAME_TO_CODE[langString.trim()]) {
    return LANGUAGE_NAME_TO_CODE[langString.trim()];
  }
  return langString;
};

const extractQualityTags = (fileName) => {
  if (!fileName) return [];
  const qualityTags = [
    "1080p",
    "720p",
    "dd5",
    "aac",
    "360p",
    "480p",
    "web-dl",
    "web_dl",
    "h264",
    "h265",
    "bluray",
    "dvdrip",
    "hdtv",
    "hevc",
    "2160p",
    "dts-hd",
    "dvb",
    "cam",
    "webdl",
    "dvd",
    "webrip",
    "web-rip",
    "x265",
    "x264",
  ];
  const lowerFileName = fileName.toLowerCase();
  return qualityTags.filter((tag) => lowerFileName.includes(tag.toLowerCase()));
};

const extractYearFromFileName = (fileName) => {
  if (!fileName) return "";
  const yearMatch = fileName.match(/[\(\[\s]((?:19|20)\d{2})[\)\]\s.]/);
  if (yearMatch) return yearMatch[1];
  const yearMatch2 = fileName.match(/(?:^|[.\s-])((?:19|20)\d{2})(?:[.\s-]|$)/);
  if (yearMatch2) return yearMatch2[1];
  return "";
};

const getUserType = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return ["user"];
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.type || ["user"];
  } catch (error) {
    return ["user"];
  }
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      return true;
    } catch (e) {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

// ==================== کامپوننت مودال جستجوی کاور ====================
const CoverSearchModal = ({ isOpen, onClose, onSelect, initialQuery }) => {
  const [activeTab, setActiveTab] = useState("search");
  const [query, setQuery] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const defaultCover = "https://umlang.com/img/srt/srt.png";

  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery || "");
      setActiveTab("search");
      setError("");
      setUploadFile(null);
      setUploadPreview("");
      setUploadError("");
      setIsDragging(false);
      if (initialQuery) {
        handleSearch(initialQuery);
      } else {
        setResults([]);
      }
    }
  }, [isOpen, initialQuery]);

  const handleSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    try {
      const result = await searchCover(q);
      if (result.success && result.images?.length > 0) {
        setResults(result.images);
      } else {
        setResults([]);
        setError("نتیجه‌ای یافت نشد");
      }
    } catch (err) {
      console.error("Cover search error:", err);
      setError(err.message || "خطا در ارتباط با سرور");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const processFile = (file) => {
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      setUploadError("فقط فایل‌های تصویری (jpg, png, webp) مجاز هستند");
      return;
    }
    if (file.size > 0.5 * 1024 * 1024) {
      setUploadError("حجم فایل نباید بیشتر از 512 کیلو بایت باشد");
      return;
    }
    setUploadFile(file);
    setUploadError("");
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.currentTarget === e.target ||
      !e.currentTarget.contains(e.relatedTarget)
    ) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setUploadError("لطفاً یک فایل انتخاب کنید");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const result = await uploadCover(
        uploadFile,
        query || initialQuery || "Unknown",
      );
      if (result.success && result.fileUri) {
        onSelect(result.fileUri);
        onClose();
      } else {
        setUploadError("خطا در آپلود فایل");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "خطا در آپلود کاور");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cover-search-overlay">
      <div className="cover-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cover-search-header">
          <h3>
            <i className="fas fa-image"></i>
            انتخاب کاور
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="cover-tabs">
          <button
            className={`cover-tab ${activeTab === "search" ? "active" : ""}`}
            onClick={() => setActiveTab("search")}
          >
            <i className="fas fa-search"></i>
            جستجو
          </button>
          <button
            className={`cover-tab ${activeTab === "upload" ? "active" : ""}`}
            onClick={() => setActiveTab("upload")}
          >
            <i className="fas fa-upload"></i>
            آپلود
          </button>
        </div>

        <div className="cover-search-body">
          {activeTab === "search" && (
            <>
              <div className="cover-search-input-group">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="نام فیلم را وارد کنید..."
                  className="cover-search-input"
                />
                <button
                  className="cover-search-btn"
                  onClick={() => handleSearch()}
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-search"></i>
                  )}
                </button>
              </div>

              {error && <div className="cover-search-error">{error}</div>}

              <div className="cover-search-results">
                <div className="cover-result-item default-cover">
                  <div
                    className="default-cover-inner"
                    onClick={() => {
                      onSelect(defaultCover);
                      onClose();
                    }}
                  >
                    <img src={defaultCover} alt="کاور پیش‌فرض" />
                    <span>کاور پیش‌فرض</span>
                  </div>
                </div>

                {results.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="cover-result-item"
                    onClick={() => {
                      onSelect(imageUrl);
                      onClose();
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`کاور ${index + 1}`}
                      onError={(e) => {
                        e.target.src = defaultCover;
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "upload" && (
            <div className="cover-upload-section">
              <div className="cover-upload-name">
                <label>نام فیلم:</label>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="نام فیلم برای آپلود"
                />
              </div>

              <div
                ref={dropZoneRef}
                className={`cover-upload-dropzone ${
                  isDragging ? "dragging" : ""
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {uploadPreview ? (
                  <div className="upload-preview-container">
                    <img src={uploadPreview} alt="پیش‌نمایش" />
                    <button
                      type="button"
                      className="btn-remove-preview"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadFile(null);
                        setUploadPreview("");
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>کلیک کنید یا فایل را بکشید</span>
                    <span className="upload-hint">
                      JPG, PNG, WebP - حداکثر 512KB
                    </span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {uploadError && (
                <div className="cover-search-error">{uploadError}</div>
              )}

              <button
                className="btn-upload-cover"
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    در حال آپلود...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    آپلود و انتخاب
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== کامپوننت اصلی ====================
const SaveCollectionModal = ({ isOpen, onClose }) => {
  const { subtitle1Json, subtitle2Json, videoMetadata } = useVideo();
  const { activeSubtitles } = useSelectedSubtitles();

  const [formData, setFormData] = useState({
    movieName: "",
    subOneID: "",
    langOne: "",
    subTwoID: "",
    langTwo: "",
    imdbId: "",
    season: "",
    episode: "",
    coverUrl: "",
    videoType: "movie",
    movieFileName: "",
    collectionType: "normalUser",
  });

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [showLangDropdown, setShowLangDropdown] = useState({
    langOne: false,
    langTwo: false,
  });
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const savedCollectionInfoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".language-selector")) {
        setShowLangDropdown({ langOne: false, langTwo: false });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSubtitleOnline = (subtitle) => {
    if (!subtitle) return false;
    if (subtitle.sourceType === "online" || subtitle.source === "online") {
      return true;
    }
    const uploadId = subtitle.subtitlesUploadId || subtitle.onlineId;
    if (uploadId && uploadId.length >= 20) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isOpen) return;
    setIsSaved(false);
    savedCollectionInfoRef.current = null;

    const types = getUserType();
    setUserTypes(types);

    const sub1 = activeSubtitles?.subtitle1;
    const sub2 = activeSubtitles?.subtitle2;

    const subOneID = sub1?.subtitlesUploadId || sub1?.onlineId || "";
    const subTwoID = sub2?.subtitlesUploadId || sub2?.onlineId || "";

    const movieName =
      sub1?.nameOfMovie || sub2?.nameOfMovie || videoMetadata.filmName || "";

    const season = sub1?.season || sub2?.season || videoMetadata.season || "";
    const episode =
      sub1?.episode || sub2?.episode || videoMetadata.episode || "";

    let videoType = "movie";
    if (season || episode) {
      videoType = "series";
    } else if (sub1?.videoType === "series" || sub2?.videoType === "series") {
      videoType = "series";
    } else if (sub1?.videoType === "serial" || sub2?.videoType === "serial") {
      videoType = "series";
    }

    const sub1Online = isSubtitleOnline(sub1);
    const sub2Online = isSubtitleOnline(sub2);

    let imdbId = "";
    let coverUrl = "";

    if (sub1Online && sub2Online) {
      imdbId = sub1?.imdbId || sub1?.imdb_id || "";
      coverUrl = sub1?.coverUrl || sub1?.cover_url || sub1?.poster || "";
    } else if (sub1Online) {
      imdbId = sub1?.imdbId || sub1?.imdb_id || "";
      coverUrl = sub1?.coverUrl || sub1?.cover_url || sub1?.poster || "";
    } else if (sub2Online) {
      imdbId = sub2?.imdbId || sub2?.imdb_id || "";
      coverUrl = sub2?.coverUrl || sub2?.cover_url || sub2?.poster || "";
    }

    const movieFileName = videoMetadata.fileName || "";

    let collectionType = "normalUser";
    if (types.includes("subtitlesManager") || types.includes("admin")) {
      collectionType = "umlang";
    }

    const rawLang1 = sub1?.language || sub1?.subtitlesLanguage || "";
    const rawLang2 = sub2?.language || sub2?.subtitlesLanguage || "";

    const normalizedLang1 = normalizeLanguageCode(rawLang1);
    const normalizedLang2 = normalizeLanguageCode(rawLang2);

    setFormData({
      movieName,
      subOneID,
      langOne: normalizedLang1,
      subTwoID,
      langTwo: normalizedLang2,
      imdbId,
      season,
      episode,
      coverUrl,
      videoType,
      movieFileName,
      collectionType,
    });
  }, [isOpen, activeSubtitles, videoMetadata]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setStatusMessage("");
      setLoading(false);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    if (isSaved) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLanguageSelect = (code, field) => {
    if (isSaved) return;
    setFormData((prev) => ({ ...prev, [field]: code }));
    setShowLangDropdown({ langOne: false, langTwo: false });
  };

  const handleCoverSelect = (url) => {
    if (isSaved) return;
    setFormData((prev) => ({ ...prev, coverUrl: url }));
  };

  const handleCopyCollectionInfo = async () => {
    const info = savedCollectionInfoRef.current;
    if (!info) return;
    const {
      collectionId,
      finalSubOneID,
      finalSubTwoID,
      movieFileName,
      season,
      episode,
      movieName,
    } = info;
    const copyInfo = `${collectionId}:${finalSubOneID}:${finalSubTwoID}:${movieFileName}:${season}:${episode}:${movieName}`;
    await copyToClipboard(copyInfo);
  };

  const handleSave = useCallback(async () => {
    setLoading(true);
    setStatusMessage("در حال بررسی زیرنویس‌ها...");

    try {
      const sub1 = activeSubtitles?.subtitle1;
      const sub2 = activeSubtitles?.subtitle2;

      const sub1IsOnline = isSubtitleOnline(sub1);
      const sub2IsOnline = isSubtitleOnline(sub2);

      let finalSubOneID = formData.subOneID;
      let finalSubTwoID = formData.subTwoID;

      if (!VALID_LANGUAGE_CODES.includes(formData.langOne)) {
        throw new Error(
          `کد زبان نامعتبر برای زیرنویس ۱: ${
            formData.langOne
          }. کدهای معتبر: ${VALID_LANGUAGE_CODES.join(", ")}`,
        );
      }
      if (!VALID_LANGUAGE_CODES.includes(formData.langTwo)) {
        throw new Error(
          `کد زبان نامعتبر برای زیرنویس ۲: ${
            formData.langTwo
          }. کدهای معتبر: ${VALID_LANGUAGE_CODES.join(", ")}`,
        );
      }

      if (!sub1IsOnline && !finalSubOneID && sub1) {
        setStatusMessage("📤 در حال آپلود زیرنویس ۱...");
        const cues = subtitle1Json || sub1.cues || [];
        const uploadResult = await uploadNonOnlineSubtitle(
          {
            language: formData.langOne,
            nameOfMovie: formData.movieName,
            season: formData.season,
            episode: formData.episode,
            otherTags: extractQualityTags(
              sub1.fileName || videoMetadata.fileName || "",
            ),
            productionYear: extractYearFromFileName(
              sub1.fileName || videoMetadata.fileName || "",
            ),
            sourceType: sub1.sourceType || sub1.source || "local",
            originalFileName: sub1.fileName || "subtitle1.srt",
            videoType: formData.videoType,
            coverUrl: formData.coverUrl || "https://umlang.com/img/srt/srt.png",
          },
          cues,
        );
        if (uploadResult.success) {
          finalSubOneID = uploadResult.subtitlesUploadId;
        } else {
          throw new Error(uploadResult.error || "خطا در آپلود زیرنویس ۱");
        }
      }

      if (!sub2IsOnline && !finalSubTwoID && sub2) {
        setStatusMessage("📤 در حال آپلود زیرنویس ۲...");
        const cues = subtitle2Json || sub2.cues || [];
        const uploadResult = await uploadNonOnlineSubtitle(
          {
            language: formData.langTwo,
            nameOfMovie: formData.movieName,
            season: formData.season,
            episode: formData.episode,
            otherTags: extractQualityTags(
              sub2.fileName || videoMetadata.fileName || "",
            ),
            productionYear: extractYearFromFileName(
              sub2.fileName || videoMetadata.fileName || "",
            ),
            sourceType: sub2.sourceType || sub2.source || "local",
            originalFileName: sub2.fileName || "subtitle2.srt",
            videoType: formData.videoType,
            coverUrl: formData.coverUrl || "https://umlang.com/img/srt/srt.png",
          },
          cues,
        );
        if (uploadResult.success) {
          finalSubTwoID = uploadResult.subtitlesUploadId;
        } else {
          throw new Error(uploadResult.error || "خطا در آپلود زیرنویس ۲");
        }
      }

      setStatusMessage("📦 در حال ایجاد کالکشن...");

      const collectionData = {
        movieName: formData.movieName,
        subOneID: finalSubOneID,
        langOne: formData.langOne,
        subTwoID: finalSubTwoID,
        langTwo: formData.langTwo,
        imdbId: formData.imdbId || "",
        season: formData.season || "",
        episode: formData.episode || "",
        coverUrl: formData.coverUrl || "https://umlang.com/img/srt/srt.png",
        videoType: formData.videoType,
        movieFileName: formData.movieFileName,
        collectionType: formData.collectionType,
      };

      const result = await createCollection(collectionData);

      if (result.success) {
        const collectionId =
          result.collectionId ||
          result.data?.collectionId ||
          result.data?._id ||
          "";
        const movieFileName =
          formData.movieFileName || videoMetadata.fileName || "";
        const movieName = formData.movieName;
        const season = formData.season || "";
        const episode = formData.episode || "";

        savedCollectionInfoRef.current = {
          collectionId,
          finalSubOneID,
          finalSubTwoID,
          movieFileName,
          season,
          episode,
          movieName,
        };

        const copyInfo = `${collectionId}:${finalSubOneID}:${finalSubTwoID}:${movieFileName}:${season}:${episode}:${movieName}`;
        await copyToClipboard(copyInfo);

        setStatusMessage("✅ کالکشن با موفقیت ذخیره شد!");
        setIsSaved(true);

        setFormData((prev) => ({
          ...prev,
          movieName: "",
          langOne: "",
          langTwo: "",
        }));
      } else {
        throw new Error(result.error || "خطا در ایجاد کالکشن");
      }
    } catch (error) {
      console.error("Error saving collection:", error);
      setStatusMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    activeSubtitles,
    subtitle1Json,
    subtitle2Json,
    videoMetadata,
    isSaved,
  ]);

  const showCollectionTypeRadio =
    userTypes.includes("subtitlesManager") || userTypes.includes("admin");

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="save-collection-overlay">
        <div
          className="save-collection-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="save-collection-header">
            <h2>
              <i className="fas fa-save"></i>
              ذخیره زیرنویس‌های فیلم
            </h2>
            <button
              className="close-btn"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="save-collection-body">
            {/* اسم فایل - بدون لیبل */}
            <div className="form-section">
              <div className="file-name-row">
                <span className="file-name-display">
                  {formData.movieFileName || "—"}
                </span>
              </div>
            </div>

            {/* آی‌دی زیرنویس‌ها - label: value کامپکت */}
            <div className="form-section">
              <div className="compact-info-row">
                <span className="compact-label">آی‌دی ۱:</span>
                <span className="compact-value">
                  {formData.subOneID ? (
                    <span className="online-id-badge" title="زیرنویس آنلاین">
                      🌐 {formData.subOneID.slice(-12)}
                    </span>
                  ) : (
                    <span className="no-id-text">نیاز به آپلود</span>
                  )}
                </span>
              </div>

              <div className="compact-info-row">
                <span className="compact-label">آی‌دی ۲:</span>
                <span className="compact-value">
                  {formData.subTwoID ? (
                    <span className="online-id-badge" title="زیرنویس آنلاین">
                      🌐 {formData.subTwoID.slice(-12)}
                    </span>
                  ) : (
                    <span className="no-id-text">نیاز به آپلود</span>
                  )}
                </span>
              </div>
            </div>

            {/* نام فیلم - label: value */}
            <div className="form-section">
              <div className="compact-form-row">
                <label htmlFor="movieName" className="compact-label">
                  نام:
                </label>
                <input
                  type="text"
                  id="movieName"
                  name="movieName"
                  value={formData.movieName}
                  onChange={handleChange}
                  placeholder="نام فیلم یا سریال"
                  required
                  disabled={isSaved}
                  className="compact-input"
                />
              </div>
            </div>

            {/* زبان‌ها در یک خط */}
            <div className="form-section">
              <div className="form-row-langs">
                <div className="lang-group">
                  <label className="compact-label">زیرنویس ۱:</label>
                  <div className="language-selector">
                    <div
                      className="language-input-wrapper"
                      onClick={() =>
                        !isSaved &&
                        setShowLangDropdown((prev) => ({
                          langOne: !prev.langOne,
                          langTwo: false,
                        }))
                      }
                    >
                      <input
                        type="text"
                        id="langOne"
                        name="langOne"
                        value={getLanguageName(formData.langOne)}
                        onChange={handleChange}
                        placeholder="انتخاب..."
                        required
                        readOnly
                        disabled={isSaved}
                      />
                      <i className="fas fa-chevron-down dropdown-arrow"></i>
                    </div>
                    {showLangDropdown.langOne && (
                      <div className="language-dropdown">
                        {getMainLanguageOptions().map((lang) => (
                          <div
                            key={lang.code}
                            className={`language-option ${
                              formData.langOne === lang.code ? "selected" : ""
                            }`}
                            onClick={() =>
                              handleLanguageSelect(lang.code, "langOne")
                            }
                          >
                            <span className="lang-name-compact">
                              {lang.code}: {lang.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="lang-group">
                  <label className="compact-label">زیرنویس ۲:</label>
                  <div className="language-selector">
                    <div
                      className="language-input-wrapper"
                      onClick={() =>
                        !isSaved &&
                        setShowLangDropdown((prev) => ({
                          langOne: false,
                          langTwo: !prev.langTwo,
                        }))
                      }
                    >
                      <input
                        type="text"
                        id="langTwo"
                        name="langTwo"
                        value={getLanguageName(formData.langTwo)}
                        onChange={handleChange}
                        placeholder="انتخاب..."
                        required
                        readOnly
                        disabled={isSaved}
                      />
                      <i className="fas fa-chevron-down dropdown-arrow"></i>
                    </div>
                    {showLangDropdown.langTwo && (
                      <div className="language-dropdown">
                        {getMainLanguageOptions().map((lang) => (
                          <div
                            key={lang.code}
                            className={`language-option ${
                              formData.langTwo === lang.code ? "selected" : ""
                            }`}
                            onClick={() =>
                              handleLanguageSelect(lang.code, "langTwo")
                            }
                          >
                            <span className="lang-name-compact">
                              {lang.code}: {lang.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* نوع ویدئو + فصل + قسمت در یک خط - label: value */}
            <div className="form-section">
              <div className="form-row-inline">
                <div className="inline-group">
                  <label htmlFor="videoType" className="compact-label">
                    نوع:
                  </label>
                  <select
                    id="videoType"
                    name="videoType"
                    value={formData.videoType}
                    onChange={handleChange}
                    disabled={isSaved}
                    className="compact-select"
                  >
                    <option value="movie">فیلم</option>
                    <option value="series">سریال</option>
                  </select>
                </div>

                {formData.videoType === "series" && (
                  <>
                    <div className="inline-group">
                      <label htmlFor="season" className="compact-label">
                        فصل:
                      </label>
                      <input
                        type="text"
                        id="season"
                        name="season"
                        value={formData.season}
                        onChange={handleChange}
                        placeholder="فصل"
                        disabled={isSaved}
                        className="compact-input-small"
                      />
                    </div>
                    <div className="inline-group">
                      <label htmlFor="episode" className="compact-label">
                        قسمت:
                      </label>
                      <input
                        type="text"
                        id="episode"
                        name="episode"
                        value={formData.episode}
                        onChange={handleChange}
                        placeholder="قسمت"
                        disabled={isSaved}
                        className="compact-input-small"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* IMDb */}
            <div className="form-section">
              <div className="compact-form-row">
                <label htmlFor="imdbId" className="compact-label">
                  IMDb:
                </label>
                <input
                  type="text"
                  id="imdbId"
                  name="imdbId"
                  value={formData.imdbId}
                  onChange={handleChange}
                  placeholder="tt1234567"
                  disabled={isSaved}
                  className="compact-input"
                />
              </div>
            </div>

            {/* کاور */}
            <div className="form-section">
              <div className="compact-form-row">
                <label htmlFor="coverUrl" className="compact-label">
                  کاور:
                </label>
                <div className="cover-input-row">
                  <input
                    type="text"
                    id="coverUrl"
                    name="coverUrl"
                    value={formData.coverUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="cover-url-input"
                    disabled={isSaved}
                  />
                  <button
                    type="button"
                    className="btn-change-cover"
                    onClick={() => setShowCoverModal(true)}
                    title="جستجو و انتخاب کاور"
                    disabled={isSaved}
                  >
                    <i className="fas fa-image"></i>
                  </button>
                </div>
              </div>
              {formData.coverUrl && (
                <div className="cover-preview">
                  <img
                    src={formData.coverUrl}
                    alt="پیش‌نمایش کاور"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <button
                    type="button"
                    className="btn-change-cover-overlay"
                    onClick={() => setShowCoverModal(true)}
                    disabled={isSaved}
                  >
                    <i className="fas fa-sync-alt"></i>
                    تغییر کاور
                  </button>
                </div>
              )}
            </div>

            {/* نوع کالکشن - label: value */}
            {showCollectionTypeRadio && (
              <div className="form-section">
                <div className="collection-type-row">
                  <span className="compact-label">نوع کالکشن:</span>
                  <label className="radio-label-inline">
                    <input
                      type="radio"
                      name="collectionType"
                      value="umlang"
                      checked={formData.collectionType === "umlang"}
                      onChange={handleChange}
                      disabled={isSaved}
                    />
                    <span>ویژه</span>
                  </label>
                  <label className="radio-label-inline">
                    <input
                      type="radio"
                      name="collectionType"
                      value="normalUser"
                      checked={formData.collectionType === "normalUser"}
                      onChange={handleChange}
                      disabled={isSaved}
                    />
                    <span>معمولی</span>
                  </label>
                </div>
              </div>
            )}

            {statusMessage && (
              <div
                className={`status-message ${
                  statusMessage.includes("✅")
                    ? "success"
                    : statusMessage.includes("❌")
                      ? "error"
                      : "info"
                }`}
                onClick={isSaved ? handleCopyCollectionInfo : undefined}
                style={isSaved ? { cursor: "pointer" } : {}}
                title={isSaved ? "کلیک کنید" : ""}
              >
                {statusMessage}
              </div>
            )}
          </div>

          <div className="save-collection-footer">
            {!isSaved ? (
              <>
                <button
                  className="btn-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  انصراف
                </button>
                <button
                  className="btn-save"
                  onClick={handleSave}
                  disabled={
                    loading ||
                    !formData.movieName ||
                    !formData.langOne ||
                    !formData.langTwo
                  }
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      در حال ذخیره...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      ذخیره کالکشن
                    </>
                  )}
                </button>
              </>
            ) : (
              <button className="btn-cancel" onClick={handleClose}>
                <i className="fas fa-times"></i>
                بستن
              </button>
            )}
          </div>
        </div>
      </div>

      <CoverSearchModal
        isOpen={showCoverModal}
        onClose={() => setShowCoverModal(false)}
        onSelect={handleCoverSelect}
        initialQuery={formData.movieName}
      />
    </>
  );
};

export default SaveCollectionModal;
